import React, { useLayoutEffect, useState, useCallback } from 'react';
import {
    SearchOutlined,
    Group,
    CloseOutlined,
    IconButton,
    Box,
    useBreakpoint,
} from '@landr/maestro';
import { t } from '@lingui/macro';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import ActiveFilters from 'components/Filters/components/ActiveFilters/ActiveFilters';
import { css } from '@landr/styled-system/css';
import TextInput from '../TextInput/TextInput';

const SearchV3Style = css({
    width: '100%',
});

const FiltersAndInput = ({
    triggerRef,
    isSuggestionsOpen,
    isModal,
    isBottomRadiusZero,
}: {
    triggerRef: React.RefObject<HTMLDivElement>;
    isSuggestionsOpen: boolean;
    isModal: boolean;
    isBottomRadiusZero: boolean;
}) => {
    const {
        clearCtaLabel,
        handleApplyTextSearch,
        handleClearCta,
        hasFilterApplied,
        hasTextInputValue,
        isSearchBarDisabled,
    } = useSearchBarContext();
    const isCloseCtaVisible = hasTextInputValue || hasFilterApplied;

    const [delayedIsBottomRadiusZero, setDelayedIsBottomRadiusZero] =
        useState(false);

    const handleRadiusChange = useCallback(() => {
        const timeoutId = setTimeout(() => {
            setDelayedIsBottomRadiusZero(false);
        }, 50);

        return () => clearTimeout(timeoutId);
    }, []);

    useLayoutEffect(() => {
        if (isSuggestionsOpen && !isModal && isBottomRadiusZero) {
            setDelayedIsBottomRadiusZero(true);
        } else {
            handleRadiusChange();
        }
    }, [isSuggestionsOpen, isModal, isBottomRadiusZero, handleRadiusChange]);

    const isMobile = useBreakpoint('md');

    return (
        <Box
            ref={triggerRef}
            translate="no"
            minHeight="md"
            display="flex"
            className={SearchV3Style}
            position="relative"
            alignItems="stretch"
            maxWidth="100%"
            backgroundColor="surface.neutral.medium"
            paddingY="xs"
            paddingX={{ base: 'none', md: 'xs' }}
            borderRadius={{ base: '3xl', md: '3xl' }}
            borderBottomLeftRadius={{
                base: delayedIsBottomRadiusZero ? 'none' : '3xl',
                md: delayedIsBottomRadiusZero ? 'none' : '3xl',
            }}
            borderBottomRightRadius={{
                base: delayedIsBottomRadiusZero ? 'none' : '3xl',
                md: delayedIsBottomRadiusZero ? 'none' : '3xl',
            }}
        >
            <Group
                alignItems="center"
                width="100%"
                paddingX="2xs"
                gap={{ base: 'none', md: 'xs' }}
            >
                <Group.Item
                    alignItems="center"
                    height="26px"
                    width="26px"
                    marginRight={isMobile ? '2xs' : 'none'}
                    marginLeft={isMobile ? 'sm' : 'none'}
                >
                    <IconButton
                        icon={SearchOutlined}
                        onClick={handleApplyTextSearch}
                        label={t`Apply Search`}
                        variant="subtle"
                        kind="link"
                        size="md"
                        paddingX={{ base: '2xs', md: 'none' }}
                        isDisabled={isSearchBarDisabled}
                        alignItems="center"
                        height="100%"
                    />
                </Group.Item>
                <Group.Item flexGrow={10}>
                    <Group
                        flexWrap="wrap"
                        gap="2xs"
                        alignItems="center"
                        paddingInline={0}
                    >
                        <ActiveFilters />
                        <TextInput />
                    </Group>
                </Group.Item>
                {isCloseCtaVisible && (
                    <Group.Item
                        height="26px"
                        width="26px"
                        alignItems="center"
                        marginRight={{ base: 'sm', md: 'none' }}
                    >
                        <IconButton
                            icon={CloseOutlined}
                            variant="subtle"
                            kind="link"
                            size="md"
                            label={clearCtaLabel}
                            paddingX="none"
                            onClick={handleClearCta}
                            height="100%"
                        />
                    </Group.Item>
                )}
            </Group>
        </Box>
    );
};

export default FiltersAndInput;
